import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { ArrowLeft, Printer, Download } from 'lucide-react';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${day}${suffix} ${months[d.getMonth()]}, ${d.getFullYear()}`;
  } catch { return dateStr; }
};

const Header = () => (
  <div className="bg-[#0052A2] text-white p-6 text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
    <div className="flex items-center justify-center gap-3 mb-1">
      <svg width="40" height="40" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="60" cy="60" r="55" stroke="white" strokeWidth="5" fill="none" />
        <path d="M38 95V40L60 20L82 40V95" stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d="M38 40L60 58L82 40" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <line x1="60" y1="58" x2="60" y2="95" stroke="white" strokeWidth="5" strokeLinecap="round" />
      </svg>
      <h2 className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>MEDICHI HOMES</h2>
    </div>
    <p className="text-blue-100 text-xs">Suite B11, Apedic Plaza Opposite Airtel Office by Stadium, Nnebisi Road, Asaba, Delta State</p>
    <p className="text-blue-200 text-xs mt-1">Tel: 09061621162 | Email: info@medichitribe.com | Web: medichi.org</p>
    <p className="text-blue-200 text-[10px] mt-1 uppercase tracking-wider">Real Estate · Investment · Construction</p>
  </div>
);

const FooterBar = () => (
  <div className="bg-[#0052A2] text-white px-6 py-3 text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
    <p className="text-[10px]">Email: info@medichitribe.com  ·  Website: medichihomes.org  ·  @medichihomes  ·  Phone: 09061621162</p>
  </div>
);

const BOAllocationLetter = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await boAPI.getAllocationLetter(clientId); setData(res.data); }
      catch (e) { toast.error('Failed to load allocation data'); navigate(-1); }
      finally { setLoading(false); }
    };
    fetch();
  }, [clientId]);

  const handlePrint = () => {
    const origTitle = document.title;
    document.title = `Medichi_Allocation_${(data?.client?.full_name || 'Client').replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => { document.title = origTitle; }, 1000);
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0052A2]" /></div></BOLayout>;
  if (!data) return null;

  const { client, plot, sale, total_paid, total_amount, balance, payment_complete, date } = data;
  const numberOfPlots = (sale?.plot_ids?.length || sale?.number_of_plots || client?.number_of_plots || 1);
  const propertyLocation = plot?.estate_location || (client?.address) || '--';

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-allocation-letter">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-neutral-100"><ArrowLeft className="h-5 w-5" /></button>
            <h1 className="text-2xl font-serif font-bold text-neutral-900">Allocation Letter</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrint} variant="outline" className="rounded-xl" data-testid="print-allocation-btn"><Printer className="h-4 w-4 mr-2" /> Print</Button>
            <Button onClick={handlePrint} className="rounded-xl bg-[#0052A2] hover:bg-[#003d7a]" data-testid="download-allocation-btn"><Download className="h-4 w-4 mr-2" /> Download PDF</Button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto print:max-w-none" id="allocation-content">
          {/* ============== PAGE 1: ALLOCATION LETTER ============== */}
          <div className="bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden print:shadow-none print:border-0 print:rounded-none page">
            <Header />

            <div className="px-8 py-4 bg-blue-50 border-b text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <h3 className="text-lg font-bold text-[#0052A2]" style={{ fontFamily: 'Georgia, serif' }}>LETTER OF ALLOCATION</h3>
            </div>

            <div className="px-8 py-6 space-y-5 text-sm leading-relaxed">
              <div className="flex justify-between">
                <div><span className="font-semibold text-[#0052A2]">Date:</span> {formatDate(date)}</div>
                <div><span className="font-semibold text-[#0052A2]">Ref:</span> MH/AL/{new Date().getFullYear()}/{client?.id?.slice(0, 6).toUpperCase()}</div>
              </div>

              <p><span className="font-semibold">Client Name:</span> {client?.full_name}</p>
              <p>Dear Sir/Ma,</p>
              <p className="font-bold text-[#0052A2] tracking-wide">ALLOCATION LETTER</p>
              <p>
                We are pleased to inform you of the allocation of <strong>{numberOfPlots}</strong> plot{numberOfPlots > 1 ? 's' : ''} in our estate with the following details:
              </p>

              <div className="ml-2 space-y-1">
                <p><strong>a)</strong> Estate Name: <span className="font-semibold">{plot?.estate_name || '--'}</span></p>
                <p><strong>b)</strong> Plot Size: <span className="font-semibold">{plot?.plot_size || '--'}</span></p>
                <p><strong>c)</strong> Plot Detail: <span className="font-semibold">{plot?.location_block || '--'}, Plot {plot?.plot_number || '--'}</span></p>
                <p><strong>d)</strong> Location: <span className="font-semibold">{propertyLocation}</span></p>
              </div>

              <p>
                You are hereby required to take possession of the property either by commencing construction or erecting a fence effective from the date of issuance of this letter.
              </p>
              <p>
                Thank you for your patronage and be rest assured that your investment will yield great returns and meet your utmost investment expectations.
              </p>

              {/* Payment status — only show as a small badge; no scary "outstanding" when paid */}
              {payment_complete ? (
                <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="allocation-paid-badge">
                  PAID IN FULL · {formatNaira(total_paid || total_amount)}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs space-y-1" data-testid="allocation-balance-box">
                  <div className="flex justify-between"><span>Total Price:</span><span className="font-semibold">{formatNaira(total_amount)}</span></div>
                  <div className="flex justify-between"><span>Amount Paid:</span><span className="font-semibold text-green-700">{formatNaira(total_paid)}</span></div>
                  <div className="flex justify-between border-t pt-1"><span>Outstanding Balance:</span><span className="font-bold text-red-600">{formatNaira(balance)}</span></div>
                </div>
              )}

              <p className="font-semibold pt-4">Best Regards,</p>

              <div className="pt-4 flex justify-between items-end">
                <div data-testid="allocation-admin-sign-block">
                  <p className="text-xs text-neutral-500 mb-2">Admin Officer</p>
                  <div className="w-56 border-b-2 border-neutral-400 mb-2 h-20" />
                  <p className="text-xs text-neutral-500">Signature</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500 mb-2">Client</p>
                  <div className="w-56 border-b-2 border-neutral-400 mb-2 h-20" />
                  <p className="text-xs text-neutral-500">{client?.full_name}'s Signature</p>
                </div>
              </div>
            </div>

            <FooterBar />
          </div>

          {/* ============== PAGE 2: TERMS & CONDITIONS ============== */}
          <div className="bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden mt-6 print:shadow-none print:border-0 print:rounded-none print:mt-0 page page-break" data-testid="allocation-terms-page">
            <Header />

            <div className="px-8 py-4 bg-blue-50 border-b text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <h3 className="text-lg font-bold text-[#0052A2]" style={{ fontFamily: 'Georgia, serif' }}>ALLOCATION TERMS &amp; CONDITIONS</h3>
            </div>

            <div className="px-8 py-6 text-sm leading-relaxed space-y-5 text-neutral-800">
              <section>
                <h4 className="font-bold text-[#0052A2] mb-2">DEFINITION OF TERMS</h4>
                <ul className="space-y-2">
                  <li><strong>"Allottee"</strong> means you, the purchaser, and, where the context so permits, any of your permitted assigns and successors-in-title.</li>
                  <li><strong>"Estate"</strong> means the model in which the property is situated.</li>
                  <li><strong>"Purchase Price"</strong> means the total amount payable by the Allottee to the designated accounts of the Vendor to acquire the Property.</li>
                  <li><strong>"Vendor"</strong> means Medichi Homes and Shelters Limited.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-[#0052A2] mb-2">ALLOTTEE'S COMMITMENT</h4>
                <ol className="list-[lower-alpha] ml-5 space-y-1">
                  <li>The Allottee is required to take possession of this property within three (3) months of being allocated, by either building or fencing.</li>
                  <li>Where the Allottee fails to carry out item (a) above, the Vendor shall not be held liable for any future encumbrance.</li>
                  <li>The Allottee undertakes not to transfer, sell, assign, convey, or otherwise deal with the property with any third party without the notice and approval of the Vendor.</li>
                </ol>
              </section>

              <section>
                <h4 className="font-bold text-[#0052A2] mb-2">VENDOR'S COMMITMENT</h4>
                <ol className="list-[lower-alpha] ml-5 space-y-1">
                  <li>The Vendor represents that the property is free from any encumbrance.</li>
                  <li>The Vendor shall not indemnify the Allottee in the event of future encumbrance.</li>
                </ol>
              </section>

              <section>
                <h4 className="font-bold text-[#0052A2] mb-2">GENERAL TERMS</h4>
                <ol className="list-[lower-alpha] ml-5 space-y-1">
                  <li>The Allocation Letter is not a title document to the Property and cannot be used by the Allottee to sell, assign, or transfer any interest to a third party in respect of the Property without the consent of the Vendor.</li>
                  <li>A title document to the Property shall be issued to the Allottee upon a successful physical allocation, and their property rights are limited to their allocated plot(s).</li>
                  <li>The Property shall be used for <strong>RESIDENTIAL AND COMMERCIAL PURPOSES</strong>.</li>
                </ol>
              </section>

              <div className="pt-4 flex justify-between items-end">
                <div>
                  <p className="text-xs text-neutral-500 mb-2">Admin Officer</p>
                  <div className="w-56 border-b-2 border-neutral-400 mb-2 h-16" />
                  <p className="text-xs text-neutral-500">Signature &amp; Date</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500 mb-2">Allottee</p>
                  <div className="w-56 border-b-2 border-neutral-400 mb-2 h-16" />
                  <p className="text-xs text-neutral-500">{client?.full_name}'s Signature &amp; Date</p>
                </div>
              </div>
            </div>

            <FooterBar />
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          body * { visibility: hidden; }
          #allocation-content, #allocation-content * { visibility: visible; }
          #allocation-content {
            position: absolute; left: 0; top: 0; width: 100%;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          #allocation-content .page { page-break-after: always; }
          #allocation-content .page:last-child { page-break-after: auto; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </BOLayout>
  );
};

export default BOAllocationLetter;
